/**
 * Entity peek card DOM (#73) for the editor's peek popover
 * (`prose/entityPeek.ts`).
 */

import { marked } from "marked";
import { isJust, matchMaybe } from "../lib/maybe";
import { nextArcMomentForPeek } from "../lib/entityArc";
import { entityTypeLabel } from "../lib/entityFrontmatter";
import {
  entityEffectiveColor,
  entityGraphNodeLabelStyle,
  entityPeekAccentStyle,
} from "../lib/entityColors";
import { isTauri } from "../lib/runtime";
import { readProjectAssetDataUrl } from "../lib/tauri/assets";
import { entityPeekProse, type EntityPeekEntry } from "./entityPeekIndex";

marked.setOptions({ gfm: true, breaks: false });

/** Hover delay before the peek opens. */
export const HOVER_TIME_MS = 400;
/** Idle caret on a matched name before showing the same peek as hover. */
export const CARET_PEEK_MS = 2500;
export const PEEK_MAX_WIDTH_PX = 500;
const PEEK_MAX_HEIGHT_PX = 240;

/** Active scene + flat manuscript order for #66 arc peek selection. */
export type EntityPeekManuscriptContext = {
  currentScenePath: string;
  sceneOrder: readonly string[];
};

export type EntityPeekCardContext = {
  /** Editor content width; the card fills up to `PEEK_MAX_WIDTH_PX` of it. */
  contentWidth: number;
  rootPath: string | null;
  manuscript: EntityPeekManuscriptContext | null;
  /** Open the entry (name button). */
  onOpen: ((fileName: string) => void) | null;
};

/** Width of the editor surface around `dom`, for sizing the card. */
export function editorContentWidth(dom: HTMLElement): number {
  const shell = dom.closest(".zw-editor-shell");
  if (shell instanceof HTMLElement && shell.clientWidth > 0) {
    return shell.clientWidth;
  }
  return dom.clientWidth > 0 ? dom.clientWidth : 480;
}

function renderLightMarkdown(source: string): string {
  try {
    return marked.parse(source, { async: false }) as string;
  } catch {
    return source;
  }
}

export function renderEntityPeekCard(
  entry: EntityPeekEntry,
  ctx: EntityPeekCardContext,
): HTMLElement {
  const root = document.createElement("div");
  root.className = "zw-entity-peek";
  // Fill to the allowed width so the name can use the full card before
  // ellipsizing, instead of shrink-wrapping to the bio line.
  const peekWidthPx = Math.min(PEEK_MAX_WIDTH_PX, ctx.contentWidth);
  root.style.width = `${peekWidthPx}px`;
  root.style.maxWidth = `${peekWidthPx}px`;
  root.style.maxHeight = `${PEEK_MAX_HEIGHT_PX}px`;

  const effectiveColor = entityEffectiveColor(entry.color, entry.type);
  matchMaybe(entityPeekAccentStyle(effectiveColor), {
    nothing: () => {},
    just: (cardStyle) => {
      const border = cardStyle.borderColor;
      if (border !== undefined) {
        root.style.borderColor = border;
      }
    },
  });
  const labelStyleM = entityGraphNodeLabelStyle(effectiveColor);

  const header = document.createElement("div");
  header.className = "zw-entity-peek-header";

  if (isJust(entry.image)) {
    const imagePath = entry.image.value;
    const avatar = document.createElement("div");
    avatar.className = "zw-entity-peek-avatar";
    avatar.hidden = true;
    const img = document.createElement("img");
    img.alt = "";
    avatar.appendChild(img);
    header.appendChild(avatar);

    const rootPath = ctx.rootPath;
    if (rootPath !== null && rootPath.length > 0 && isTauri()) {
      void readProjectAssetDataUrl(rootPath, imagePath)
        .then((src) => {
          if (!root.isConnected) return;
          img.src = src;
          avatar.hidden = false;
        })
        .catch(() => {
          /* omit avatar when the asset is missing */
        });
    }
  }

  const meta = document.createElement("div");
  meta.className = "zw-entity-peek-meta";

  const nameRow = document.createElement("div");
  nameRow.className = "zw-entity-peek-name-row";

  const nameBtn = document.createElement("button");
  nameBtn.type = "button";
  nameBtn.className = "zw-entity-peek-name";
  nameBtn.textContent = entry.displayName;
  matchMaybe(labelStyleM, {
    nothing: () => {},
    just: (labelStyle) => {
      nameBtn.style.color = labelStyle.label;
    },
  });
  nameBtn.addEventListener("mousedown", (event) => {
    // Keep focus in the editor; just navigate on click.
    event.preventDefault();
  });
  nameBtn.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    ctx.onOpen?.(entry.fileName);
  });
  nameRow.appendChild(nameBtn);

  matchMaybe(entityTypeLabel(entry.type), {
    nothing: () => {},
    just: (typeLabel) => {
      const typeEl = document.createElement("div");
      typeEl.className = "zw-entity-peek-type";
      typeEl.textContent = typeLabel;
      matchMaybe(labelStyleM, {
        nothing: () => {},
        just: (labelStyle) => {
          typeEl.style.color = labelStyle.type;
        },
      });
      nameRow.appendChild(typeEl);
    },
  });

  meta.appendChild(nameRow);

  if (isJust(entry.type) && entry.type.value === "character") {
    const manuscript = ctx.manuscript;
    if (manuscript !== null) {
      const arcMomentM = nextArcMomentForPeek(
        entry.arc,
        manuscript.currentScenePath,
        manuscript.sceneOrder,
      );
      matchMaybe(arcMomentM, {
        nothing: () => {},
        just: (arcMoment) => {
          const arcEl = document.createElement("div");
          arcEl.className = "zw-entity-peek-arc";
          matchMaybe(labelStyleM, {
            nothing: () => {},
            just: (labelStyle) => {
              arcEl.style.color = labelStyle.label;
            },
          });
          arcEl.textContent = arcMoment.moment;
          meta.appendChild(arcEl);
        },
      });
    }
  }

  header.appendChild(meta);
  root.appendChild(header);

  const prose = entityPeekProse(entry);
  if (prose.length > 0) {
    const body = document.createElement("div");
    body.className = "zw-entity-peek-body";
    body.innerHTML = renderLightMarkdown(prose);
    root.appendChild(body);
  }

  return root;
}
